import { useCallback, useEffect, useRef, useState } from 'react'

const CHECKOUT_STARTED_KEY = 'mf365_checkout_started'
const EXIT_OFFER_SHOWN_KEY = 'mf365_exit_offer_shown'
const EXIT_SENTINEL_KEY = 'mf365_exit_sentinel'

export function markCheckoutStarted() {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(CHECKOUT_STARTED_KEY, 'true')
  } catch {
    // The checkout remains available even if session storage is unavailable.
  }
}

export function useExitIntent() {
  const [isOpen, setIsOpen] = useState(false)
  const hasInteractedRef = useRef(false)
  const hasSpentTimeRef = useRef(false)
  const hasReachedPricingRef = useRef(false)
  const isShownRef = useRef(false)
  const lastScrollPositionRef = useRef(0)

  const hasCheckoutStarted = useCallback(() => {
    try {
      return window.sessionStorage.getItem(CHECKOUT_STARTED_KEY) === 'true'
    } catch {
      return false
    }
  }, [])

  const canShowOffer = useCallback(() => {
    return (
      hasCheckoutStarted() ||
      hasReachedPricingRef.current ||
      (hasInteractedRef.current && hasSpentTimeRef.current)
    )
  }, [hasCheckoutStarted])

  const showOffer = useCallback(() => {
    if (isShownRef.current || !canShowOffer()) return

    isShownRef.current = true
    try {
      window.sessionStorage.setItem(EXIT_OFFER_SHOWN_KEY, 'true')
    } catch {
      // The in-memory guard still ensures the modal appears only once per visit.
    }
    setIsOpen(true)
  }, [canShowOffer])

  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      isShownRef.current =
        window.sessionStorage.getItem(EXIT_OFFER_SHOWN_KEY) === 'true'
    } catch {
      isShownRef.current = false
    }

    if (!window.history.state?.[EXIT_SENTINEL_KEY]) {
      window.history.pushState(
        { ...window.history.state, [EXIT_SENTINEL_KEY]: true },
        '',
        window.location.href,
      )
    }

    const markInteraction = () => {
      hasInteractedRef.current = true
    }
    const markTimeOnPage = () => {
      hasSpentTimeRef.current = true
    }
    const handleScroll = () => {
      hasInteractedRef.current = true
      lastScrollPositionRef.current = window.scrollY
    }
    lastScrollPositionRef.current = window.scrollY
    const pricingSection = document.getElementById('pricing')
    const pricingObserver = pricingSection
      ? new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              hasReachedPricingRef.current = true
              lastScrollPositionRef.current = window.scrollY
            }
          },
          { threshold: 0.2 },
        )
      : null

    if (pricingSection) pricingObserver?.observe(pricingSection)

    const handleMouseLeave = (event: MouseEvent) => {
      if (event.clientY <= 0) showOffer()
    }
    const handlePopState = () => {
      // Arriving at #pricing is internal navigation, not an exit attempt.
      // Returning from that section removes the hash and can show the offer.
      if (window.location.hash || !canShowOffer() || isShownRef.current) return

      const scrollPosition = lastScrollPositionRef.current || window.scrollY
      window.history.pushState(
        { ...window.history.state, [EXIT_SENTINEL_KEY]: true },
        '',
        window.location.href,
      )
      window.setTimeout(
        () => window.scrollTo({ top: scrollPosition, behavior: 'auto' }),
        0,
      )
      showOffer()
    }

    const timer = window.setTimeout(markTimeOnPage, 25000)
    window.addEventListener('pointerdown', markInteraction, { passive: true })
    window.addEventListener('keydown', markInteraction)
    window.addEventListener('scroll', handleScroll, { passive: true })
    document.addEventListener('mouseleave', handleMouseLeave)
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.clearTimeout(timer)
      pricingObserver?.disconnect()
      window.removeEventListener('pointerdown', markInteraction)
      window.removeEventListener('keydown', markInteraction)
      window.removeEventListener('scroll', handleScroll)
      document.removeEventListener('mouseleave', handleMouseLeave)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [canShowOffer, showOffer])

  const closeOffer = useCallback(() => {
    setIsOpen(false)
  }, [])

  return { closeOffer, isOpen }
}
